import React, { useCallback, useEffect, useState, useMemo } from 'react'
import { Box, Flex } from '@moonpig/launchpad-components'
import {
  breakpoint,
  breakpointDown,
  styled,
  css,
} from '@moonpig/launchpad-utils'
import { spacingPx } from '@moonpig/launchpad-theme'
import { system as s } from '@moonpig/launchpad-system'
import {
  ConciergeButton,
  Header as LaunchpadHeader,
} from '@moonpig/web-core-nav'
import {
  createGenericEvent,
  createGenericSelectContentEvent,
  createNavigationEvent,
  trackGAEvent,
} from '@moonpig/web-core-analytics'
import { createLinkHref } from '@moonpig/web-core-link'
import { Region } from '@moonpig/web-core-types'
import {
  CookieCategory,
  getBrowserCookie,
  setBrowserCookie,
} from '@moonpig/web-core-browser-storage'
import { LogoLink } from '@moonpig/web-core-brand/components'
import { useNotificationsContext } from '@moonpig/web-core-context'
import { SearchBar } from '@moonpig/web-core-app-shell-search'
import { useLoggedIn } from '@moonpig/web-core-auth'
import { useLocaleText } from '../../AppShell.locale'
import { buildTrackedNavItems, NavItem } from './data/trackedNavItems'
import { TrackedToggle } from './data/trackedToggle'
import { useStoreSelectorConfig } from './useStoreSelectorConfig'
import { createUtilityItems } from './createUtilityItems'
import { useBasketTotalItems } from '../../hooks/useBasketTotalItems'
import { useFetchReminders } from './reminderNotification/useFetchReminders'
import {
  saveInLocalStorage,
  shouldShowNotificationBadgeForReminders,
} from './reminderNotification/shouldShowNotificationBadgeForReminders'
import { useCreditAmountConfig } from './credit/useCreditAmountConfig'
import { useIsMobile } from '../../hooks/useIsMobile'
import { HideSearchBarOption } from './types'
import { useReturnUrl } from './useReturnUrl'

const StyledHeaderSearch = styled(Flex)`
  align-items: center;
  width: 100%;
  ${({ theme }) =>
    s({
      gap: spacingPx(3)({ theme }),
    })}

  ${breakpoint('md')} {
    align-items: stretch;
  }

  ${css`
    ${breakpointDown('md')} {
      ${s({ px: 6, my: 3 })}
    }
  `}
`

const StyledSearchBar = styled(Box)`
  flex: 1;
  min-width: 0;

  ${css`
    ${breakpointDown('md')} {
      ${s({ p: 0, m: 0 })}
    }
  `}
`

const buildHomeLink = (region: Region) => {
  const options = {
    url: `/${region}/`,
    trackEvent: createNavigationEvent({
      action: 'logos',
      label: 'Moonpig Logo',
    }),
  }

  const result = {
    href: createLinkHref(options),
  }
  return result
}

const shouldHideSearchBar = (
  isMobile: boolean,
  hideOption?: HideSearchBarOption,
) => {
  switch (hideOption) {
    case 'all':
      return true
    case 'mobile':
      return isMobile
    case 'desktop':
      return !isMobile
    default:
      return false
  }
}

export const Header: React.FC<{
  region: Region
  navItems: NavItem[]
  searchDepartment?: {
    name: string
    title: string
  }
  searchTerm?: string
  headerLogoIconUrl?: string
  hideSearchBar?: HideSearchBarOption
  nonStickyHeader?: boolean
  isHeaderSignInExperimentEnabled: boolean
  isConciergeSearchExperimentEnabled: boolean
}> = ({
  region,
  navItems,
  searchDepartment,
  searchTerm,
  hideSearchBar,
  headerLogoIconUrl,
  nonStickyHeader,
  isHeaderSignInExperimentEnabled,
  isConciergeSearchExperimentEnabled,
}) => {
  const t = useLocaleText()
  const reminders = useFetchReminders()
  const creditConfig = useCreditAmountConfig({ region })
  const isMobile = useIsMobile()
  const { loggedIn } = useLoggedIn()

  const isSearchBarHidden = useMemo(
    () => shouldHideSearchBar(isMobile, hideSearchBar),
    [hideSearchBar, isMobile],
  )

  const [openedNotificationsOnce, setOpenedNotificationsOnce] = useState<
    boolean | null
  >(null)
  useEffect(() => {
    const openedOnce =
      getBrowserCookie('mnpg_show_notification_badge') === 'false'
    setOpenedNotificationsOnce(openedOnce)
  }, [openedNotificationsOnce])

  const showNotificationBadge =
    shouldShowNotificationBadgeForReminders(reminders) ||
    openedNotificationsOnce === false

  const notifications = useNotificationsContext()

  const onClick = useCallback(() => {
    trackGAEvent(
      createGenericEvent({
        eventAction: 'icon',
        eventLabel: 'Reminders',
        eventCategory: 'navigation',
      }),
    )
    setBrowserCookie(
      'mnpg_show_notification_badge',
      'false',
      {
        path: '/',
        maxAge: 2592000,
      },
      CookieCategory.STRICTLY_NECESSARY,
    )
    setOpenedNotificationsOnce(true)
    saveInLocalStorage(reminders)
  }, [reminders])

  const onDismiss = useCallback<(trackEvent: boolean) => void>(trackEvent => {
    if (trackEvent) {
      return trackGAEvent(
        createGenericSelectContentEvent({
          eventCategory: 'reminders',
          eventAction: 'toolbar close',
          eventLabel: 'reminder toolbar',
        }),
      )
    }
  }, [])

  const notifier = {
    notifications: notifications.notifications,
    onClick,
    onDismiss,
    showNotificationBadge,
  }

  const storeSelectorConfig = useStoreSelectorConfig()

  const utilityItems = createUtilityItems({
    region,
    loggedIn,
    returnUrl: useReturnUrl(),
    localisedLabels: {
      basket: t('basket'),
      account: t('account'),
      login: t('sign_in'),
    },
    basketTotalItems: useBasketTotalItems().basketTotalItems,
    isHeaderSignInExperimentEnabled,
    isConciergeSearchExperimentEnabled,
  })

  const headerProps = {
    navItems: buildTrackedNavItems(navItems),
    logoLink: (
      <LogoLink
        href={buildHomeLink(region).href}
        title={t('logo_link')}
        complementaryImageUrl={headerLogoIconUrl}
      />
    ),
    utilityItems,
    toggleAs: TrackedToggle,
    notifier,
    storeSelectorConfig,
    experimentCreditAmountConfig: creditConfig,
    nonStickyHeader,
  }

  return (
    <LaunchpadHeader {...headerProps}>
      <StyledHeaderSearch>
        {!isSearchBarHidden && (
          <StyledSearchBar>
            <SearchBar
              searchDepartment={searchDepartment}
              searchTerm={searchTerm}
              key={searchTerm}
            />
          </StyledSearchBar>
        )}
        {isConciergeSearchExperimentEnabled && <ConciergeButton />}
      </StyledHeaderSearch>
    </LaunchpadHeader>
  )
}
